import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import styled, { css } from "styled-components";
import { DateIcon } from "assets/icons";
import Calendar from "../Calendar";
import { DATE_FORMAT } from "config/constant";
import DocumentTextField from "../DocumentTextField/DocumentTextField";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ComponentUsageType } from "models/document/ComponentUsageType";

interface DatePickerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    StyledWrapProps {
  dateFormat?: string;
  inputComponent?: React.ReactNode;
  defaultValue?: string;
  value?: string;
  usage?: ComponentUsageType;
  placeholder?: string;
  isTimePicker?: boolean;
  errorMessage?: string;
  onChangeDate?: (date: Date, dateString: string) => void;
}

/**
 * @param {function} onChangeDate DatePicker로 특정 날짜를 선택하면 해당 날짜를 인자로 반환합니다.
 */
const DatePicker = ({
  dateFormat = DATE_FORMAT,
  disabled,
  inputComponent,
  usage,
  defaultValue,
  value,
  placeholder,
  isTimePicker,
  errorMessage,
  onChangeDate,
  ...rest
}: DatePickerProps) => {
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    defaultValue ? dayjs(defaultValue).toDate() : undefined,
  );
  const [label, setLabel] = useState<string | undefined>(
    defaultValue ? dayjs(defaultValue).format(dateFormat) : undefined,
  );

  /**
   * @description DatePicker의 다른 날짜를 선택했을 때의 이벤트 핸들러
   */
  const handleChangeDate = (selectedDate: Date) => {
    setSelectedDate(selectedDate);

    const newLabel = dayjs(selectedDate).format(dateFormat);
    setLabel(newLabel);

    if (onChangeDate) onChangeDate(selectedDate, newLabel);
  };

  const [open, setOpen] = useState(false);
  const onCloseCalendar = () => setOpen(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <WrapStyle ref={datePickerRef} {...rest}>
        <DropdownMenu.Trigger asChild>
          {usage === "document" ? (
            <DocumentTextField
              leftIcon={<DateIcon />}
              type="text"
              placeholder={placeholder}
              readOnly
              value={value || label}
              errorMessage={errorMessage}
            />
          ) : (
            <InputWrap filled={!!selectedDate} disabled={disabled}>
              <Date>{value || label}</Date>
              <DateIcon />
            </InputWrap>
          )}
        </DropdownMenu.Trigger>

        <DropdownMenu.Content asChild side="bottom" align="start">
          <CalendarContainer>
            <Calendar
              isTimePicker={isTimePicker}
              selectedDate={selectedDate}
              onChangeDate={handleChangeDate}
              onCloseCalendar={onCloseCalendar}
            />
          </CalendarContainer>
        </DropdownMenu.Content>
      </WrapStyle>
    </DropdownMenu.Root>
  );
};

export default DatePicker;

/* 스타일 */

const semanticTokens = {
  padding: "1.2rem 1.4rem",
};

interface StyledWrapProps {
  disabled?: boolean;
}

const WrapStyle = styled.div`
  min-width: 16rem;
  position: relative;
`;

const InputWrap = styled.div<StyledWrapProps & { filled?: boolean }>`
  background: ${({ theme }) => theme.color.background.white};
  width: 100%;
  height: 4.8rem;
  padding: ${semanticTokens.padding};
  border: 0.1rem solid ${({ theme }) => theme.color.stroke.light};
  border-radius: 0.5rem;
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.text.placeholder};
  cursor: pointer;

  display: inline-flex;
  align-items: center;

  svg {
    color: ${({ theme }) => theme.color.text.tertiery};
  }

  /* status: disabled */
  ${({ theme, disabled }) =>
    disabled
      ? css`
          background: ${theme.color.background.default};
          color: ${theme.color.text.disabled};
          cursor: not-allowed;
        `
      : css`
          /* status: hover */
          &:hover {
            ${({ theme }) => css`
              border-color: ${theme.color.stroke.dark};
              color: ${theme.color.text.secondary};

              svg {
                color: ${theme.color.text.secondary};
              }
            `}
          }
        `}

  /* status: filled */
  ${({ theme, filled }) =>
    filled &&
    css`
      &,
      &:hover {
        color: ${theme.color.text.default};
      }
    `}
`;

const Date = styled.div`
  flex: 1;
  ${({ theme }) => theme.typography.body1.regular}
`;

const CalendarContainer = styled.div`
  position: absolute;
  top: calc(100% + 0.9rem);
  left: 0;
  z-index: 800;
`;
